import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccessibleIcon from '@mui/icons-material/Accessible';
import WcIcon from '@mui/icons-material/Wc';
import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import MainLayout from './layout/MainLayout';
import { brand } from '../theme';
import { FESTIVAL_HOURS_LABEL } from '../utils/festivalDates';

const VENUE = {
  name: 'George County Fairgrounds',
  addressLine1: '9162 Old Hwy 63',
  addressLine2: 'Lucedale, MS 39452',
  searchQuery: '9162 Old Hwy 63, Lucedale, MS 39452',
};

const encodedQuery = encodeURIComponent(VENUE.searchQuery);
const appleMapsUrl = `https://maps.apple.com/?daddr=${encodedQuery}`;
const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedQuery}`;
const embedSrc = `https://www.google.com/maps?q=${encodedQuery}&output=embed`;

const Directions = () => {
  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}>
        <Typography variant="h1" sx={{ color: brand.navy, mb: 1.5, textAlign: 'center' }}>
          Getting to Gingham Tree
        </Typography>
        <Typography
          sx={{
            textAlign: 'center',
            color: brand.textSecondary,
            fontSize: '1.1875rem',
            maxWidth: 560,
            mx: 'auto',
            mb: { xs: 4, sm: 5 },
          }}
        >
          The festival is held at the George County Fairgrounds in Lucedale,
          Mississippi ({FESTIVAL_HOURS_LABEL}). Free parking is available on-site.
        </Typography>

        {/* Venue card with prominent address and two large "Open in Maps"
            buttons so a tap on a phone hands off to the native maps app. */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.paper,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                backgroundColor: brand.cream,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: brand.navy,
                flexShrink: 0,
              }}
            >
              <LocationOnIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h3"
                sx={{ color: brand.navy, mb: 0.5 }}
              >
                {VENUE.name}
              </Typography>
              <Typography sx={{ fontSize: '1.125rem', color: brand.textPrimary }}>
                {VENUE.addressLine1}
              </Typography>
              <Typography sx={{ fontSize: '1.125rem', color: brand.textPrimary }}>
                {VENUE.addressLine2}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mt: { xs: 3, sm: 4 },
            }}
          >
            <Button
              component="a"
              href={appleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AppleIcon />}
              sx={{ py: 2, fontSize: '1.0625rem' }}
            >
              Open in Apple Maps
            </Button>
            <Button
              component="a"
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<GoogleIcon />}
              sx={{ py: 2, fontSize: '1.0625rem' }}
            >
              Open in Google Maps
            </Button>
          </Box>
        </Paper>

        {/* Embedded map. The Google "output=embed" endpoint doesn't require
            an API key and works well in an iframe. */}
        <Box
          sx={{
            mt: { xs: 4, sm: 5 },
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${brand.hairline}`,
          }}
        >
          <Box
            component="iframe"
            title="Map to George County Fairgrounds"
            src={embedSrc}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            sx={{
              border: 0,
              width: '100%',
              height: { xs: 320, sm: 420 },
              display: 'block',
            }}
          />
        </Box>

        {/* Parking section. Copy mirrors the existing Visitors page so the
            two sources stay in sync. */}
        <Box sx={{ mt: { xs: 5, sm: 7 } }}>
          <Typography variant="h2" sx={{ color: brand.navy, mb: 2 }}>
            Parking
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              border: `1px solid ${brand.hairline}`,
              backgroundColor: brand.cream,
            }}
          >
            <List sx={{ p: 0 }}>
              <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
                <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                  <DirectionsCarIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Free on-site parking"
                  secondary="Parking is free and organized by attendants on festival day. Please follow their direction so traffic keeps flowing."
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '1.125rem' }}
                  secondaryTypographyProps={{ fontSize: '1.0625rem', color: brand.textPrimary }}
                />
              </ListItem>
              <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
                <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                  <AccessibleIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Accessible parking"
                  secondary="Designated accessible parking spaces are available near the main entrance. Attendants can guide you to the closest space."
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '1.125rem' }}
                  secondaryTypographyProps={{ fontSize: '1.0625rem', color: brand.textPrimary }}
                />
              </ListItem>
            </List>
          </Paper>
        </Box>

        {/* Day-of arrival tips so visitors know what to expect. */}
        <Box sx={{ mt: { xs: 5, sm: 7 } }}>
          <Typography variant="h2" sx={{ color: brand.navy, mb: 2 }}>
            On the day
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              border: `1px solid ${brand.hairline}`,
              backgroundColor: brand.paper,
            }}
          >
            <List sx={{ p: 0 }}>
              <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
                <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                  <AccessTimeIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Festival hours"
                  secondary={`Gingham Tree runs ${FESTIVAL_HOURS_LABEL} on the second Saturday of November. Arriving when gates open gives you the calmest parking and the best selection at every booth.`}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '1.125rem' }}
                  secondaryTypographyProps={{ fontSize: '1.0625rem', color: brand.textPrimary }}
                />
              </ListItem>
              <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
                <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>
                  <WcIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Restrooms"
                  secondary="Restrooms are available inside and outside the Jaycees Building and at both arena concession stands."
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '1.125rem' }}
                  secondaryTypographyProps={{ fontSize: '1.0625rem', color: brand.textPrimary }}
                />
              </ListItem>
            </List>
          </Paper>
        </Box>
      </Container>
    </MainLayout>
  );
};

export default Directions;
